// src/pages/DeviceTypeManagement/DeviceTypeDetails.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { deviceTypesApi } from '../../services/apiClient';
import OverviewTab from './tabs/OverviewTab';
import ServicesTab from './tabs/ServicesTab';
import MonitoringTab from './tabs/MonitoringTab';

const DeviceTypeDetails = ({ deviceType, onUpdate, onDelete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deviceTypeData, setDeviceTypeData] = useState(deviceType);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setDeviceTypeData(deviceType);
    setHasChanges(false);
  }, [deviceType]);

  const handleDataChange = (updates) => {
    setDeviceTypeData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await deviceTypesApi.update(deviceType.id, deviceTypeData);

      if (response.data.success) {
        enqueueSnackbar('Device type updated successfully', { variant: 'success' });
        setHasChanges(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating device type:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to update device type',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${deviceType.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await deviceTypesApi.delete(deviceType.id);

      if (response.data.success) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting device type:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete device type',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h6">{deviceType.FrontEndName || deviceType.friendlyName || `Device Type ${deviceType.id}`}</Typography>
          <Typography variant="caption" color="text.secondary">
            Device Type ID: {deviceType.id} | Friendly Name: {deviceType.friendlyName || 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab label="Services" />
          <Tab label="Monitoring" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {activeTab === 0 && (
          <OverviewTab
            deviceType={deviceTypeData}
            onChange={handleDataChange}
          />
        )}
        {activeTab === 1 && (
          <ServicesTab
            deviceType={deviceTypeData}
            onChange={handleDataChange}
          />
        )}
        {activeTab === 2 && (
          <MonitoringTab
            deviceType={deviceTypeData}
            onChange={handleDataChange}
          />
        )}
      </Box>
    </Box>
  );
};

export default DeviceTypeDetails;
